import { Directive, input, signal } from '@angular/core';

import { injectSchemaActions } from './schema-action';

/**
 * Runs a named action when its element is clicked.
 *
 * The name is the component's own word for what happened — `click` for a generic button, `delete` or
 * `edit` for a component that owns several triggers. It is a literal in the template, never a value
 * from the schema: what a control does is the component's business, and what that means is the
 * schema's, which is the split the whole actions design rests on.
 *
 * ```html
 * <button type="button" [krosSchemaAction]="'delete'" [krosSchemaActionPayload]="row()">…</button>
 * ```
 *
 * Firing **stops propagation**, so a control inside a clickable container does not also run the
 * container's action. That is the one rule replacing the ownership filtering an earlier draft put in
 * the renderer: an element that handles a click says so by carrying this directive.
 *
 * Bind `undefined` to make it inert — no run, no warning, no propagation stopped. That is how a
 * container is clickable only when the schema gave it something to do.
 */
@Directive({
  selector: '[krosSchemaAction]',
  exportAs: 'krosSchemaAction',
  host: {
    '(click)': 'onClick($event)',
  },
})
export class SchemaActionDirective {
  /** Which action to run. `undefined` makes the directive inert. */
  readonly action = input<string | undefined>(undefined, { alias: 'krosSchemaAction' });

  /** Passed to the action as `context.payload`. */
  readonly payload = input<unknown>(undefined, { alias: 'krosSchemaActionPayload' });

  readonly #actions = injectSchemaActions();

  readonly #running = signal(false);

  /** Whether a run is in flight, for a `[disabled]` binding through `#ref="krosSchemaAction"`. */
  readonly isRunning = this.#running.asReadonly();

  protected onClick(event: Event): void {
    const action = this.action();

    if (!action) {
      return;
    }

    event.stopPropagation();

    if (this.#running()) {
      return;
    }

    void this.#run(action);
  }

  async #run(action: string): Promise<void> {
    this.#running.set(true);

    try {
      await this.#actions.runAction(action, this.payload());
    } finally {
      this.#running.set(false);
    }
  }
}
